import { removeAllWhiteSpace, replaceAll, trimLeftCharKeepOne } from '@shared/helpers/utils.helper';
import { DecimalBody } from '@shared/models/interfaces/decimal-body.interface';
import { SanitizeNumber } from '@shared/models/interfaces/sanitize-number.interface';
import { numericWithOptionalDecimalRegex } from '@shared/constants/regex.const';
import { ThousandSeparators } from '@shared/models/interfaces/thousand-separators.interface';

export const applyLimits = (value: string, maxIntegerDigits: number, maxFractionDigits: number, decimalSep: string): string => {
    const sign = value.startsWith('-') ? '-' : '';
    const body = sign ? value.slice(1) : value;
    const { intPart: rawInt, fracPart: rawFrac, separatorChar } = splitDecimal(body, decimalSep);
    let intPart = trimLeftCharKeepOne(rawInt ?? '', '0');
    let frac = rawFrac ?? '';

    if (maxIntegerDigits > 0 && intPart.length > maxIntegerDigits) {
        intPart = intPart.slice(0, maxIntegerDigits);
    }

    if (maxFractionDigits >= 0 && frac.length > maxFractionDigits) {
        frac = frac.slice(0, maxFractionDigits);
    }

    const hasSep = !!separatorChar;
    const rebuilt = hasSep ? `${intPart}${decimalSep}${frac}` : intPart;
    if (sign && rebuilt.length === 0) {
        return '-';
    }

    return sign ? `${sign}${rebuilt}` : rebuilt;
};

export const sanitize = (value: string, allowNegative: boolean, groupSep: string, decimalSep: string, maxFractionDigits: number): SanitizeNumber => {
    let v = removeAllWhiteSpace(value ?? '');
    const negative = allowNegative && v.startsWith('-');
    v = replaceAll(v, '-', '');
    v = [...v].filter((ch) => (ch >= '0' && ch <= '9') || ch === '.' || ch === ',').join('');
    if ((groupSep === '.' || groupSep === ',') && groupSep !== decimalSep) {
        v = replaceAll(v, groupSep, '');
    }

    const otherDec = decimalSep === '.' ? ',' : '.';
    v = replaceAll(v, otherDec, '');
    const first = v.indexOf(decimalSep);
    if (first !== -1) {
        const before = v.slice(0, first + 1);
        const after = replaceAll(v.slice(first + 1), decimalSep, '');
        v = before + after;
    }

    if (!maxFractionDigits || maxFractionDigits <= 0) {
        v = v.split(decimalSep)[0];
    }

    return { negative, body: v };
};

export const splitDecimal = (body: string, decimalSep: string): DecimalBody => {
    const idx = body.indexOf(decimalSep);
    if (idx === -1) {
        return { intPart: body, fracPart: '', separatorChar: null };
    }

    const intPart = body.slice(0, idx);
    const fracPart = body.slice(idx + 1);

    return { intPart, fracPart, separatorChar: decimalSep };
};

export const computeLocaleSeps = (locale: string): ThousandSeparators => {
    const parts = new Intl.NumberFormat(locale).formatToParts(12345.6);
    const dec = parts.find((p) => p.type === 'decimal')?.value ?? ',';
    const grp = parts.find((p) => p.type === 'group')?.value ?? '.';

    return {
        decimalSep: dec === '.' ? '.' : ',',
        groupSep: grp,
    };
};

export const countTokensBeforeCaret = (value: string, caretPos: number, decimalSep: string): number => {
    const end = Math.min(caretPos, value.length);
    const part = value.slice(0, end);
    let tokens = 0;
    for (let i = 0; i < part.length; i++) {
        const ch = part[i];
        if ((ch >= '0' && ch <= '9') || ch === decimalSep) {
            tokens++;
        }
    }
    return tokens;
};

export const caretFromTokens = (value: string, tokenTarget: number, decimalSep: string): number => {
    if (tokenTarget <= 0) {
        return 0;
    }

    let seen = 0;
    for (let i = 0; i < value.length; i++) {
        const ch = value[i];
        if ((ch >= '0' && ch <= '9') || ch === decimalSep) {
            seen++;
            if (seen >= tokenTarget) return i + 1;
        }
    }
    return value.length;
};

export const updateLimitHint = (typed: string, maxIntegerDigits: number): string | null => {
    const sign = typed.startsWith('-') ? '-' : '';
    const body = sign ? typed.slice(1) : typed;
    const m = body.match(numericWithOptionalDecimalRegex);
    if (!m) {
        return null;
    }

    const intPart = m[1] ?? '';
    const intAtMax = maxIntegerDigits > 0 && intPart.length >= maxIntegerDigits;
    return intAtMax ? `Massimo ${maxIntegerDigits} cifre intere.` : null;
};

/**
 * Normalizes a value coming from the store/backend (e.g. via writeValue) so that a dot-decimal
 * value like "1234.56" is not destroyed by the locale-aware sanitize (which strips the group
 * separator). Only the incoming value is normalized — typed input is always locale-formatted.
 *
 * Rule: if the value contains no locale decimal separator and exactly one group-separator char,
 * that char is a decimal point → convert it to the locale decimal separator. Locale-formatted
 * values (containing the decimal separator) and grouping-ambiguous values are left untouched.
 */
export const normalizeIncomingDecimal = (value: string, decimalSep: string, groupSep: string): string => {
    if (!value || decimalSep === groupSep) {
        return value;
    }

    const hasLocaleDecimal = value.includes(decimalSep);
    const groupSepCount = value.split(groupSep).length - 1;
    if (!hasLocaleDecimal && groupSepCount === 1) {
        return replaceAll(value, groupSep, decimalSep);
    }

    return value;
};
